import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';

const YELLOW = '#F4C430';

const trimOrNull = (v) => {
  const s = (v ?? '').trim();
  return s === '' ? null : s;
};

/**
 * Edit the identity fields of an inventory item: name, barcode, product ID, notes.
 * Category and printer-linking are managed through other UI.
 */
export const EditItemModal = ({ open, onClose, onSaved, item }) => {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open && item) {
      setForm({
        name:      item.name ?? '',
        barcode:   item.barcode ?? '',
        productId: item.productId ?? '',
        notes:     item.notes ?? '',
      });
      setError(null);
    }
  }, [open, item]);

  if (!open || !item) return null;

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.currentTarget.value }));

  const save = async () => {
    if (!form.name?.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.patch(`/inventory/${item.id}`, {
        name:      form.name.trim(),
        barcode:   trimOrNull(form.barcode),
        productId: trimOrNull(form.productId),
        notes:     trimOrNull(form.notes),
      });
      onSaved?.(updated);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: YELLOW }}>Edit item</p>
          <p class="m-0 font-serif text-lg font-medium text-text-primary">{item.name}</p>
        </div>

        <div class="space-y-4 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Name</p>
            <input
              type="text"
              value={form.name ?? ''}
              autoFocus
              onInput={set('name')}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Barcode</p>
              <input
                type="text"
                value={form.barcode ?? ''}
                onInput={set('barcode')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 font-mono text-[13px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Product ID</p>
              <input
                type="text"
                value={form.productId ?? ''}
                onInput={set('productId')}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-1.5 font-mono text-[13px] font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
              Notes <span class="font-medium text-text-muted">· optional</span>
            </p>
            <textarea
              rows={3}
              value={form.notes ?? ''}
              onInput={set('notes')}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
            </p>
          )}
        </div>

        <div class="flex justify-end gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" onClick={save} disabled={busy || !form.name?.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};
